/**
 * The debounced, coalescing autosave queue behind the Concept Workbench.
 *
 * Spec §6.8 splits the surface into two save models on purpose: the concept
 * BRIEF is an explicit, versioned, human-reviewed Save, and layer state is a
 * private high-frequency artifact that saves itself 800ms after you stop moving.
 * This is the second half. It is deliberately transport-agnostic — it knows
 * nothing about Supabase — so the coalescing and in-flight rules can be tested
 * with fake timers instead of a mocked database.
 *
 * Three rules, each of which exists because breaking it produces a real bug:
 *
 *  1. **Trailing debounce per key.** A drag emits ~60 geometry patches a second.
 *     Firing each one would be a write storm; firing the leading edge would
 *     persist the first frame of a drag and none of the rest.
 *
 *  2. **Patches COALESCE, they do not queue.** Ten patches to one rendition
 *     become one write of their merge. This is what makes the optimistic lock
 *     workable at all: one write per row per window means one version bump per
 *     window instead of ten CAS rounds fighting each other.
 *
 *  3. **Never two writes in flight for the same key.** A CAS carries an expected
 *     version; two overlapping writes would both read the same expected version
 *     and the second would always lose. Anything enqueued during a flush waits
 *     for it to settle and then fires as its own window, picking up the version
 *     the first write returned.
 */

export const AUTOSAVE_DEBOUNCE_MS = 800;

export interface AutosaveQueueOptions<Patch> {
  /** Idle time before a key's pending patch is written. Defaults to 800ms. */
  delayMs?: number;
  /** Fold a newer patch onto an older pending one. Later values win. */
  merge: (pending: Patch, incoming: Patch) => Patch;
  /** Perform the write. Rejecting routes to `onError`; the patch is not retried. */
  flush: (key: string, patch: Patch) => Promise<void>;
  /**
   * Called when `flush` rejects. There is no automatic retry: a rejected layer
   * save is either a conflict the caller must resolve or an offline transport,
   * and silently retrying a stale patch forever is how a conflict becomes a
   * clobber.
   */
  onError?: (key: string, error: unknown, patch: Patch) => void;
  /** Fires whenever the set of keys with unsaved work changes. */
  onPendingChange?: (keys: string[]) => void;
}

export interface AutosaveQueue<Patch> {
  enqueue: (key: string, patch: Patch) => void;
  /** Write one key's pending patch now. Resolves once it has settled. */
  flush: (key: string) => Promise<void>;
  /** Write every pending key now. Resolves when the queue is empty. */
  flushAll: () => Promise<void>;
  /** Drop pending work without writing. For unmount-after-conflict only. */
  cancel: (key?: string) => void;
  pendingKeys: () => string[];
  hasPending: (key?: string) => boolean;
  isFlushing: (key?: string) => boolean;
}

interface Entry<Patch> {
  patch: Patch;
  timer: ReturnType<typeof setTimeout> | null;
}

export function createAutosaveQueue<Patch>(
  options: AutosaveQueueOptions<Patch>,
): AutosaveQueue<Patch> {
  const delayMs = options.delayMs ?? AUTOSAVE_DEBOUNCE_MS;
  const pending = new Map<string, Entry<Patch>>();
  const inFlight = new Map<string, Promise<void>>();

  function announce(): void {
    options.onPendingChange?.([...pending.keys()]);
  }

  function clearTimer(entry: Entry<Patch>): void {
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
  }

  async function write(key: string): Promise<void> {
    const entry = pending.get(key);
    if (!entry) return;

    // Rule 3. Chain behind the in-flight write rather than racing it; the patch
    // stays pending so the follow-up window picks it up with a fresh version.
    const running = inFlight.get(key);
    if (running) {
      await running.catch(() => undefined);
      return write(key);
    }

    clearTimer(entry);
    pending.delete(key);
    announce();

    const task = options
      .flush(key, entry.patch)
      .catch((error: unknown) => {
        options.onError?.(key, error, entry.patch);
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, task);
    await task;
  }

  return {
    enqueue(key, patch) {
      const entry = pending.get(key);
      if (entry) {
        entry.patch = options.merge(entry.patch, patch);
        clearTimer(entry);
        entry.timer = setTimeout(() => void write(key), delayMs);
        return;
      }
      const next: Entry<Patch> = { patch, timer: null };
      next.timer = setTimeout(() => void write(key), delayMs);
      pending.set(key, next);
      announce();
    },

    async flush(key) {
      await write(key);
      const still = inFlight.get(key);
      if (still) await still;
    },

    async flushAll() {
      // Loop rather than a single pass: a flush can resolve while another key's
      // debounce is still counting down, and "flushAll resolved with unsaved
      // work left" is precisely the false pass a beforeunload handler must not
      // make.
      while (pending.size > 0 || inFlight.size > 0) {
        await Promise.all([...pending.keys()].map((key) => write(key)));
        await Promise.all([...inFlight.values()].map((task) => task.catch(() => undefined)));
      }
    },

    cancel(key) {
      if (key === undefined) {
        for (const entry of pending.values()) clearTimer(entry);
        pending.clear();
        announce();
        return;
      }
      const entry = pending.get(key);
      if (!entry) return;
      clearTimer(entry);
      pending.delete(key);
      announce();
    },

    pendingKeys: () => [...pending.keys()],
    hasPending: (key) => (key === undefined ? pending.size > 0 : pending.has(key)),
    isFlushing: (key) => (key === undefined ? inFlight.size > 0 : inFlight.has(key)),
  };
}

/**
 * The domain merge for a rendition patch. Geometry and overrides are keyed maps
 * and must merge PER LAYER, not wholesale: dragging layer A then editing an
 * override on layer B inside one window must not drop A's move.
 */
export function mergeKeyedPatch<
  Patch extends { geometry?: Record<string, unknown>; overrides?: Record<string, unknown> },
>(pending: Patch, incoming: Patch): Patch {
  const merged: Record<string, unknown> = { ...pending, ...incoming };
  if (pending.geometry || incoming.geometry) {
    merged.geometry = { ...pending.geometry, ...incoming.geometry };
  }
  if (pending.overrides || incoming.overrides) {
    merged.overrides = { ...pending.overrides, ...incoming.overrides };
  }
  return merged as Patch;
}
